import { useEffect, useState } from 'react';

import {
  apiFetch,
  isAdsEnabled,
  isInteriorEnabled,
  isMobileEnabled,
  isVideoEnabled,
} from '../../utils/api';
import { fetchAllJobsInRange, jobCostEntries } from '../../hooks/useStats';
import { fetchPricingSettings } from '../../hooks/useSettings';
import type { DateRange, HistoricalJob } from '../../types';

import { listAllHistoryJobs as listAllVideoHistory } from '../../video/api';
import { jobEndpointCosts } from '../../video/utils/falPricing';
import type { Job as VideoJob } from '../../video/types';

import { listHistoryJobs as listInteriorHistory } from '../../interior/api';
import type { Job as InteriorJob } from '../../interior/types';

import { listHistoryJobs as listAdsHistory } from '../../ads/api';
import type { Job as AdsJob } from '../../ads/types';

import {
  fetchAllJobsInRange as fetchAllMobileJobsInRange,
  jobCostEntries as mobileJobCostEntries,
} from '../../mobile/hooks/useMobileStats';
import { fetchPricingSettings as fetchMobilePricingSettings } from '../../mobile/hooks/useMobileSettings';

import { fetchStats, fetchStatsByJob } from '../../apigen/api';
import type { ApiProviderCostRow } from '../../apigen/types';

import type { CostItem, MerchCostItem, MerchCostTotals, ServiceCost } from '../types';
import {
  VendorTally,
  vendorForApigenProvider,
  vendorForGeminiModel,
  vendorForUsageEndpoint,
} from '../vendors';

// ---------------------------------------------------------------------------
// GCP flat cost per scan — stored as a single row in the lightning
// `pricing_settings` table (provider `infra`, key `gcp_cost_per_scan`) so it
// is configurable without a new table/endpoint. The lightning pricing UI keys
// off known FAL/OpenAI/Gemini endpoints, so this extra row is inert there.
// ---------------------------------------------------------------------------

export const GCP_COST_PROVIDER = 'infra';
export const GCP_COST_KEY = 'gcp_cost_per_scan';
/** Fallback used until The Master configures a real figure. */
export const DEFAULT_GCP_COST_PER_SCAN = 0.1;

interface RawPricingRow {
  provider: string;
  endpoint_or_model: string;
  price_per_call: number | null;
}

export async function fetchGcpCostPerScan(): Promise<number> {
  const res = await apiFetch('/api/settings/pricing');
  if (!res.ok) return DEFAULT_GCP_COST_PER_SCAN;
  const rows = (await res.json()) as RawPricingRow[];
  const row = rows.find(
    (r) => r.provider === GCP_COST_PROVIDER && r.endpoint_or_model === GCP_COST_KEY,
  );
  return row?.price_per_call != null ? Number(row.price_per_call) : DEFAULT_GCP_COST_PER_SCAN;
}

export async function saveGcpCostPerScan(value: number): Promise<void> {
  const res = await apiFetch('/api/settings/pricing', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      settings: [
        {
          provider: GCP_COST_PROVIDER,
          endpoint_or_model: GCP_COST_KEY,
          price_per_call: value,
          price_per_input_token: null,
          price_per_output_token: null,
        },
      ],
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Failed to save GCP cost (${res.status})`);
  }
}

// ---------------------------------------------------------------------------
// Taggers flat cost per scan — stored the same way as the GCP cost (an `infra`
// row in `pricing_settings`, key `taggers_cost_per_scan`), and allocated flatly
// to every completed merch scan alongside the GCP cost.
// ---------------------------------------------------------------------------

export const TAGGERS_COST_PROVIDER = 'infra';
export const TAGGERS_COST_KEY = 'taggers_cost_per_scan';
/** Fallback used until The Master configures a real figure. */
export const DEFAULT_TAGGERS_COST_PER_SCAN = 0;

export async function fetchTaggersCostPerScan(): Promise<number> {
  const res = await apiFetch('/api/settings/pricing');
  if (!res.ok) return DEFAULT_TAGGERS_COST_PER_SCAN;
  const rows = (await res.json()) as RawPricingRow[];
  const row = rows.find(
    (r) => r.provider === TAGGERS_COST_PROVIDER && r.endpoint_or_model === TAGGERS_COST_KEY,
  );
  return row?.price_per_call != null ? Number(row.price_per_call) : DEFAULT_TAGGERS_COST_PER_SCAN;
}

export async function saveTaggersCostPerScan(value: number): Promise<void> {
  const res = await apiFetch('/api/settings/pricing', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      settings: [
        {
          provider: TAGGERS_COST_PROVIDER,
          endpoint_or_model: TAGGERS_COST_KEY,
          price_per_call: value,
          price_per_input_token: null,
          price_per_output_token: null,
        },
      ],
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Failed to save taggers cost (${res.status})`);
  }
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function toMs(value: string | null | undefined, fallback: number): number {
  if (!value) return fallback;
  const t = Date.parse(value);
  return Number.isNaN(t) ? fallback : t;
}

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function sumCost(items: CostItem[]): number {
  return items.reduce((acc, it) => acc + it.cost, 0);
}

/**
 * Read `direct_cost` / `calls` off a loosely-typed usage blob. The ads job rows
 * type their usage maps as `Record<string, unknown>`, so the fields have to be
 * narrowed rather than cast.
 */
function readUsageBlob(usage: unknown): { cost: number; calls: number } {
  if (!usage || typeof usage !== 'object') return { cost: 0, calls: 0 };
  const u = usage as { direct_cost?: unknown; calls?: unknown };
  return {
    cost: typeof u.direct_cost === 'number' ? u.direct_cost : 0,
    calls: typeof u.calls === 'number' ? u.calls : 0,
  };
}

/**
 * Per-job cost plus a vendor tally, for the services whose spend is recorded as
 * `direct_cost` against a FAL-style endpoint map (interior, ads, video).
 */
function tallyDirectCostUsage(
  tally: VendorTally,
  usage: Record<string, unknown> | null | undefined,
  vendorOverride?: ReturnType<typeof vendorForUsageEndpoint>,
): number {
  let cost = 0;
  for (const [endpoint, blob] of Object.entries(usage ?? {})) {
    const { cost: entryCost, calls } = readUsageBlob(blob);
    tally.add(vendorOverride ?? vendorForUsageEndpoint(endpoint), entryCost, calls, endpoint);
    cost += entryCost;
  }
  return cost;
}

/**
 * Cost of one lightning-style job (merch, mobile) with each priced usage line
 * attributed to its vendor. The returned total is the sum of the same lines, so
 * the vendor split always reconciles with the job's cost.
 */
function tallyPricedJob(
  tally: VendorTally,
  entries: { key: string; source: 'fal_usage' | 'gemini_usage'; calls: number; cost: number }[],
): number {
  let cost = 0;
  for (const entry of entries) {
    const vendor =
      entry.source === 'gemini_usage' ? vendorForGeminiModel() : vendorForUsageEndpoint(entry.key);
    tally.add(vendor, entry.cost, entry.calls, entry.key);
    cost += entry.cost;
  }
  return cost;
}

/** A service that failed to load: zeroed out, with the reason attached. */
function unavailableService(
  key: ServiceCost['key'],
  label: string,
  unit: ServiceCost['unit'],
  warning: string,
): ServiceCost {
  return { key, label, unit, count: 0, totalCost: 0, items: [], vendors: [], warning };
}

async function fetchAllInteriorJobs(dateRange: DateRange): Promise<InteriorJob[]> {
  const all: InteriorJob[] = [];
  const pageSize = 500;
  let page = 1;
  let totalPages = 1;
  do {
    const resp = await listInteriorHistory(dateRange, null, page, pageSize);
    all.push(...resp.jobs);
    totalPages = resp.total_pages;
    if (resp.jobs.length === 0) break;
    page += 1;
  } while (page <= totalPages);
  return all;
}

async function fetchAllAdsJobs(dateRange: DateRange): Promise<AdsJob[]> {
  const all: AdsJob[] = [];
  const pageSize = 500;
  let page = 1;
  let totalPages = 1;
  do {
    const resp = await listAdsHistory(dateRange, null, page, pageSize);
    all.push(...resp.jobs);
    totalPages = resp.total_pages;
    if (resp.jobs.length === 0) break;
    page += 1;
  } while (page <= totalPages);
  return all;
}

/**
 * Pick a gen-api server-side bucket width (seconds) that keeps the series
 * payload bounded (~<=1500 points) for the requested window. The Cost pages
 * re-bucket client-side anyway, so a coarser server bucket is lossless here.
 */
function genapiBucketSeconds(from: Date, to: Date): number {
  const windowSec = Math.max(1, Math.round((to.getTime() - from.getTime()) / 1000));
  return Math.min(86_400, Math.max(60, Math.ceil(windowSec / 1500)));
}

// ---------------------------------------------------------------------------
// per-service builders (each fails independently → non-fatal warning)
// ---------------------------------------------------------------------------

async function buildMerch(dateRange: DateRange, defaultTs: number): Promise<ServiceCost> {
  try {
    const [pricing, jobs] = await Promise.all([
      fetchPricingSettings().catch(() => []),
      fetchAllJobsInRange(dateRange),
    ]);
    const completed = jobs.filter((j) => j.status === 'completed');
    const tally = new VendorTally();
    const items: CostItem[] = completed.map((j) => ({
      t: toMs(j.completed_at ?? j.created_at, defaultTs),
      cost: tallyPricedJob(tally, jobCostEntries(j, pricing)),
    }));
    return {
      key: 'merch',
      label: 'Merch',
      unit: 'scans',
      count: completed.length,
      totalCost: sumCost(items),
      items,
      vendors: tally.toList(),
    };
  } catch (e) {
    return unavailableService('merch', 'Merch', 'scans', errMsg(e));
  }
}

async function buildVideo(dateRange: DateRange, defaultTs: number): Promise<ServiceCost> {
  try {
    const jobs = await listAllVideoHistory(dateRange);
    const completed = jobs.filter((j: VideoJob) => j.status === 'completed');
    const tally = new VendorTally();
    const items: CostItem[] = completed.map((j) => {
      let cost = 0;
      for (const ec of jobEndpointCosts(j)) {
        const calls = j.fal_usage?.[ec.endpoint]?.calls ?? 0;
        tally.add(vendorForUsageEndpoint(ec.endpoint), ec.cost, calls, ec.endpoint);
        cost += ec.cost;
      }
      return { t: toMs(j.completed_at ?? j.created_at, defaultTs), cost };
    });
    return {
      key: 'video',
      label: '360 Video',
      unit: 'scans',
      count: completed.length,
      totalCost: sumCost(items),
      items,
      vendors: tally.toList(),
    };
  } catch (e) {
    return unavailableService('video', '360 Video', 'scans', errMsg(e));
  }
}

async function buildInterior(dateRange: DateRange, defaultTs: number): Promise<ServiceCost> {
  try {
    const jobs = await fetchAllInteriorJobs(dateRange);
    const completed = jobs.filter((j) => j.status === 'completed');
    const tally = new VendorTally();
    const items: CostItem[] = completed.map((j) => ({
      t: toMs(j.completed_at ?? j.created_at, defaultTs),
      cost: tallyDirectCostUsage(tally, j.fal_usage),
    }));
    return {
      key: 'interior',
      label: 'Interior',
      unit: 'scans',
      count: completed.length,
      totalCost: sumCost(items),
      items,
      vendors: tally.toList(),
    };
  } catch (e) {
    return unavailableService('interior', 'Interior', 'scans', errMsg(e));
  }
}

async function buildAds(dateRange: DateRange, defaultTs: number): Promise<ServiceCost> {
  try {
    const jobs = await fetchAllAdsJobs(dateRange);
    const completed = jobs.filter((j) => j.status === 'completed');
    const tally = new VendorTally();
    const items: CostItem[] = completed.map((j) => ({
      t: toMs(j.completed_at ?? j.created_at, defaultTs),
      cost:
        tallyDirectCostUsage(tally, j.fal_usage) +
        tallyDirectCostUsage(tally, j.gemini_usage, vendorForGeminiModel()),
    }));
    return {
      key: 'ads',
      label: 'Ads',
      unit: 'scans',
      count: completed.length,
      totalCost: sumCost(items),
      items,
      vendors: tally.toList(),
    };
  } catch (e) {
    return unavailableService('ads', 'Ads', 'scans', errMsg(e));
  }
}

async function buildMobile(dateRange: DateRange, defaultTs: number): Promise<ServiceCost> {
  try {
    const [pricing, jobs] = await Promise.all([
      fetchMobilePricingSettings().catch(() => []),
      fetchAllMobileJobsInRange(dateRange),
    ]);
    const completed = jobs.filter((j) => j.status === 'completed');
    const tally = new VendorTally();
    const items: CostItem[] = completed.map((j) => ({
      t: toMs(j.completed_at ?? j.created_at, defaultTs),
      cost: tallyPricedJob(tally, mobileJobCostEntries(j, pricing)),
    }));
    return {
      key: 'mobile',
      label: 'Mobile',
      unit: 'scans',
      count: completed.length,
      totalCost: sumCost(items),
      items,
      vendors: tally.toList(),
    };
  } catch (e) {
    return unavailableService('mobile', 'Mobile', 'scans', errMsg(e));
  }
}

async function buildGenApi(from: Date, to: Date): Promise<ServiceCost> {
  try {
    const stats = await fetchStats({ from, to, bucketSeconds: genapiBucketSeconds(from, to) });
    const items: CostItem[] = stats.series.map((p) => ({
      t: p.ts ? Date.parse(p.ts) : from.getTime(),
      cost: p.cost_usd || 0,
    }));
    // `by_provider` is absent on gen-api builds that predate the split; the
    // vendor breakdown then renders as unavailable rather than being guessed.
    const tally = new VendorTally();
    for (const row of stats.by_provider ?? []) {
      tally.add(
        vendorForApigenProvider(row.provider),
        row.cost_usd,
        row.total,
        row.provider || 'unknown provider',
      );
    }
    return {
      key: 'genapi',
      label: 'Gen-API',
      unit: 'calls',
      count: stats.totals.total,
      totalCost: stats.totals.cost_usd,
      items,
      vendors: tally.toList(),
    };
  } catch (e) {
    return unavailableService('genapi', 'Gen-API', 'calls', `gen-api unreachable: ${errMsg(e)}`);
  }
}

// ---------------------------------------------------------------------------
// hooks
// ---------------------------------------------------------------------------

export interface AllServicesCostResult {
  services: ServiceCost[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Cost across every service (merch/360/interior/ads/mobile/gen-api) for a window.
 * Each service is fetched independently so one being down (or disabled in a
 * dev build) never blanks the whole page.
 */
export function useAllServicesCost(range: { from: Date; to: Date }): AllServicesCostResult {
  const [services, setServices] = useState<ServiceCost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const fromMs = range.from.getTime();
  const toMs2 = range.to.getTime();

  useEffect(() => {
    let cancelled = false;
    // A degenerate window means the caller has no range to load yet (e.g. a
    // half-filled custom date pair) — show nothing rather than query epoch 0.
    if (fromMs >= toMs2) {
      setServices([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const from = new Date(fromMs);
    const to = new Date(toMs2);
    const dateRange: DateRange = { from, to };
    const defaultTs = toMs2;

    const builders: Promise<ServiceCost>[] = [buildMerch(dateRange, defaultTs)];
    if (isVideoEnabled()) builders.push(buildVideo(dateRange, defaultTs));
    if (isInteriorEnabled()) builders.push(buildInterior(dateRange, defaultTs));
    if (isAdsEnabled()) builders.push(buildAds(dateRange, defaultTs));
    if (isMobileEnabled()) builders.push(buildMobile(dateRange, defaultTs));
    builders.push(buildGenApi(from, to));

    Promise.all(builders)
      .then((results) => {
        if (!cancelled) setServices(results);
      })
      .catch((e) => {
        if (!cancelled) setError(errMsg(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fromMs, toMs2, reloadKey]);

  return { services, loading, error, reload: () => setReloadKey((k) => k + 1) };
}

export interface MerchCostResult {
  items: MerchCostItem[];
  totals: MerchCostTotals;
  gcpCostPerScan: number;
  taggersCostPerScan: number;
  genApiWarning: string | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  saveGcp: (value: number) => Promise<void>;
  saveTaggers: (value: number) => Promise<void>;
}

const EMPTY_TOTALS: MerchCostTotals = {
  scans: 0,
  merchGen: 0,
  genApi: 0,
  gcp: 0,
  taggers: 0,
  grand: 0,
  avgPerScan: 0,
  vendors: [],
};

/**
 * Per-merch-scan cost combining generation cost (8 angles), correlated
 * gen-api spend for the same job id, and a flat configurable GCP cost.
 */
export function useMerchCost(range: { from: Date; to: Date }): MerchCostResult {
  const [items, setItems] = useState<MerchCostItem[]>([]);
  const [totals, setTotals] = useState<MerchCostTotals>(EMPTY_TOTALS);
  const [gcpCostPerScan, setGcpCostPerScan] = useState<number>(DEFAULT_GCP_COST_PER_SCAN);
  const [taggersCostPerScan, setTaggersCostPerScan] = useState<number>(DEFAULT_TAGGERS_COST_PER_SCAN);
  const [genApiWarning, setGenApiWarning] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const fromMs = range.from.getTime();
  const toMs2 = range.to.getTime();

  useEffect(() => {
    let cancelled = false;
    // See `useAllServicesCost`: an empty window means "nothing selected yet".
    if (fromMs >= toMs2) {
      setItems([]);
      setTotals(EMPTY_TOTALS);
      setError(null);
      setGenApiWarning(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setGenApiWarning(null);
    const from = new Date(fromMs);
    const to = new Date(toMs2);
    const dateRange: DateRange = { from, to };

    (async () => {
      try {
        const [pricing, merchJobs, gcp, taggers] = await Promise.all([
          fetchPricingSettings().catch(() => []),
          fetchAllJobsInRange(dateRange),
          fetchGcpCostPerScan().catch(() => DEFAULT_GCP_COST_PER_SCAN),
          fetchTaggersCostPerScan().catch(() => DEFAULT_TAGGERS_COST_PER_SCAN),
        ]);

        // gen-api correlation is best-effort (separate service).
        const byJob = new Map<string, number>();
        const providersByJob = new Map<string, ApiProviderCostRow[]>();
        let warning: string | null = null;
        try {
          const resp = await fetchStatsByJob({ from, to });
          for (const row of resp.by_job) {
            byJob.set(row.job_id, row.cost_usd);
            if (row.by_provider) providersByJob.set(row.job_id, row.by_provider);
          }
        } catch (e) {
          warning = `gen-api unreachable: ${errMsg(e)}`;
        }

        const completed = merchJobs.filter((j: HistoricalJob) => j.status === 'completed');
        const vendorTally = new VendorTally();
        const built: MerchCostItem[] = completed.map((j) => {
          const merchGen = tallyPricedJob(vendorTally, jobCostEntries(j, pricing));
          const genApi = byJob.get(j.job_id) ?? 0;
          const genApiProviders = providersByJob.get(j.job_id);
          if (genApiProviders) {
            for (const row of genApiProviders) {
              vendorTally.add(
                vendorForApigenProvider(row.provider),
                row.cost_usd,
                row.calls,
                `gen-api: ${row.provider || 'unknown provider'}`,
              );
            }
          } else if (genApi > 0) {
            // A gen-api build without the per-provider split: keep the spend in
            // the breakdown rather than dropping it, but don't invent a vendor.
            vendorTally.add('other', genApi, 0, 'gen-api (provider unknown)');
          }
          vendorTally.add('gcp', gcp, 0, GCP_COST_KEY);
          vendorTally.add('taggers', taggers, 0, TAGGERS_COST_KEY);
          const total = merchGen + genApi + gcp + taggers;
          return {
            jobId: j.job_id,
            scanId: j.scan_id,
            t: toMs(j.completed_at ?? j.created_at, toMs2),
            merchGen,
            genApi,
            gcp,
            taggers,
            total,
          };
        });

        const agg = built.reduce(
          (acc, it) => {
            acc.merchGen += it.merchGen;
            acc.genApi += it.genApi;
            acc.gcp += it.gcp;
            acc.taggers += it.taggers;
            return acc;
          },
          { merchGen: 0, genApi: 0, gcp: 0, taggers: 0 },
        );
        const grand = agg.merchGen + agg.genApi + agg.gcp + agg.taggers;
        const nextTotals: MerchCostTotals = {
          scans: built.length,
          merchGen: agg.merchGen,
          genApi: agg.genApi,
          gcp: agg.gcp,
          taggers: agg.taggers,
          grand,
          avgPerScan: built.length ? grand / built.length : 0,
          vendors: vendorTally.toList(),
        };

        if (!cancelled) {
          setItems(built);
          setTotals(nextTotals);
          setGcpCostPerScan(gcp);
          setTaggersCostPerScan(taggers);
          setGenApiWarning(warning);
        }
      } catch (e) {
        if (!cancelled) {
          setError(errMsg(e));
          setItems([]);
          setTotals(EMPTY_TOTALS);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fromMs, toMs2, reloadKey]);

  const saveGcp = async (value: number) => {
    await saveGcpCostPerScan(value);
    setReloadKey((k) => k + 1);
  };

  const saveTaggers = async (value: number) => {
    await saveTaggersCostPerScan(value);
    setReloadKey((k) => k + 1);
  };

  return {
    items,
    totals,
    gcpCostPerScan,
    taggersCostPerScan,
    genApiWarning,
    loading,
    error,
    reload: () => setReloadKey((k) => k + 1),
    saveGcp,
    saveTaggers,
  };
}
